/**
 * Pulls the EMR page's own DOM recording into the replay.
 *
 * The recorder in ./recorder.js records the Comprehend app's document. In the
 * extension that document is a cross-origin iframe, so it can never see the EMR
 * — and the EMR chart is the thing an injector bug is actually about. So the
 * content script records the EMR page itself (chromeServices/BugCapture.ts) into
 * a local buffer, and this pump drains that buffer into the replay as `cpt-emr`
 * custom events.
 *
 * PULL, not push. The panel already has a working request path to the page in
 * both surfaces — chrome.tabs.sendMessage in the extension, postMessage through
 * the parent in the embed host — and both are reached by the same
 * runEMRFunction() wrapper. Pushing would have meant inventing a second,
 * surface-specific channel for the content script to reach the panel, including
 * the embed case where the content script has no handle on the iframe at all.
 *
 * Everything here is best-effort: on any failure the pump keeps its interval and
 * tries again on the next tick. A replay missing its EMR side is worth less, but
 * a replay that breaks the app is worth less than nothing.
 */

import { noteEmrDomBatch } from './sidecar';
import recorder from './recorder';
import { getEmrReplayEvents } from '../../EMR_Integration/react_webInjector.ts';

// Long enough that draining is cheap next to the EMR's own work, short enough
// that a tab closed without warning loses only a few seconds of chart.
const DRAIN_INTERVAL_MS = 5000;

let timer = null;
let draining = false;
let consecutiveFailures = 0;
let generation = 0;

// After this many consecutive failures the pump stops. The usual cause is a page
// with no Comprehend content script (the user navigated the tab off the EMR), and
// polling a page that will never answer for the rest of a two-hour session is
// just noise in their console.
const MAX_CONSECUTIVE_FAILURES = 5;

// The EMR recorder re-snapshots only when asked (see EmrReplayRecorder.ts), and
// this pump is what asks. A snapshot is needed when the batches before it are
// unusable to the viewer: at the start of a replay, in case the EMR recorder was
// still running from a previous one; after a chunk failed to upload, taking its
// EMR batches with it; after the EMR buffer dropped events; and when the drained
// page changes under us (a second EMR tab). Never more than one per
// CHECKOUT_MIN_INTERVAL_MS, so a page that overflows the EMR buffer on every
// drain re-snapshots no more often than the old fixed interval did.
const CHECKOUT_MIN_INTERVAL_MS = 60_000;
let checkoutPending = false;
let lastCheckoutAt = 0;
let lastLostChunks = 0;
let lastDrainUrl = null;

async function drainOnce() {
  // Automatic recorder stops (buffer ceiling, chunk cap) bypass index.js, so
  // notice them here and stop polling; the EMR recorder's own idle timeout then
  // releases it.
  if (!recorder.isRecording || recorder.stopping) {
    stopEmrDomPump();
    return;
  }
  // Overlapping drains would double-report events: the content script clears its
  // buffer when it hands it over, but a second call can land before the first
  // returns on a slow page.
  if (draining) return;
  draining = true;
  const drainGeneration = generation;
  const replayId = recorder.replayId;
  const wantsCheckout = checkoutPending || (recorder.lostChunks || 0) > lastLostChunks;
  const checkout = wantsCheckout && Date.now() - lastCheckoutAt >= CHECKOUT_MIN_INTERVAL_MS;
  // Counted from the request, not the response: a drain that times out (the
  // embed bridge gives up after 5 s, and the batch carrying a snapshot is the
  // slowest one) has still cost the page a snapshot, so the next tick must not
  // ask again. The request stays pending for the next interval instead.
  if (checkout) lastCheckoutAt = Date.now();
  try {
    const batch = await getEmrReplayEvents({ checkout });
    // The pump was stopped, restarted, or moved to another replay while this
    // request was in flight: its answer belongs to the old stream.
    if (drainGeneration !== generation || replayId !== recorder.replayId ||
        !recorder.isRecording || recorder.stopping) return;
    if (checkout) {
      checkoutPending = false;
      lastLostChunks = recorder.lostChunks || 0;
    }
    // Drops reported by a checkout drain happened before the snapshot it
    // carries, so that batch already stands on its own.
    if (!checkout && batch && batch.meta && batch.meta.droppedEvents > 0) checkoutPending = true;
    const events = batch && batch.events;
    // The drain reaches whichever EMR tab is active, so a chart tab and a
    // schedule tab arrive as one stream: after a switch, this batch was recorded
    // against a different page than the one before it. Ask for a snapshot
    // unless the batch already carries one (a reloaded tab restarts fresh).
    const url = batch && batch.meta && batch.meta.url;
    if (url && lastDrainUrl && url !== lastDrainUrl &&
        !(Array.isArray(events) && events.some((event) => event && event.type === 2))) {
      checkoutPending = true;
    }
    if (url) lastDrainUrl = url;
    if (Array.isArray(events) && events.length) {
      noteEmrDomBatch(events, batch.meta || null);
    }
    consecutiveFailures = 0;
  } catch (e) {
    if (drainGeneration !== generation) return;
    consecutiveFailures += 1;
    if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
      console.log('replay: EMR DOM pump giving up (no reachable EMR page)', e);
      stopEmrDomPump();
    }
  } finally {
    draining = false;
  }
}

/** Begin draining. Idempotent. */
export function startEmrDomPump() {
  if (timer) return;
  generation += 1;
  consecutiveFailures = 0;
  checkoutPending = true;
  lastCheckoutAt = 0;
  lastLostChunks = recorder.lostChunks || 0;
  lastDrainUrl = null;
  timer = setInterval(drainOnce, DRAIN_INTERVAL_MS);
  // Take the first sample immediately: the opening full snapshot of the EMR page
  // is the most valuable single item in the stream, and waiting a full interval
  // for it loses the state the user was in when recording began.
  drainOnce();
}

/** Stop draining, and take one last sample so the tail is not lost. */
export function stopEmrDomPump({ finalDrain = false } = {}) {
  if (!finalDrain) generation += 1;
  if (timer) {
    clearInterval(timer);
    timer = null;
  }
  if (finalDrain) return drainOnce();
  return undefined;
}

export function isEmrDomPumpRunning() {
  return timer !== null;
}
